import type { ISecodaEditor, MenuItem } from '../..';

import { Box, createStyles } from '@mantine/core';
import { useEffect } from 'react';
import dictionary from '../../dictionary';
import { Toolbar } from '../../types';
import { VisuallyHidden } from '../VisuallyHidden';
import { CommandMenuDivider } from './CommandMenuDivider';
import { useFloatingToolbarContext } from './context';
import { FloatingToolbar } from './FloatingToolbar';
import { LinkInput } from './LinkInput';
import { useCommandMenu } from './useCommandMenu';
import { useToolbarSearch } from './useToolbarSearch';

const useStyles = createStyles((theme) => ({
	scroll: {
		overflow: 'hidden',
		overflowY: 'auto',
		padding: `${theme.spacing['2xs']} 0`,
	},
}));

export interface CommandMenuProps<T extends MenuItem = MenuItem> {
	id: string;
	editor: ISecodaEditor;
	search: string;
	renderMenuItem: (
		item: T,
		index: number,
		props: Record<string, unknown>
	) => React.ReactNode;
	filterable?: boolean;
	items: T[];
	trigger?: string;
	toolbar: Toolbar;
}

function CommandMenuInternal({
	items,
	trigger,
	renderMenuItem,
	editor,
	search,
	filterable,
	toolbar,
}: CommandMenuProps) {
	const { classes } = useStyles();

	const { getItemProps, activeIndex, elementsRef, reset, refs } =
		useFloatingToolbarContext();

	const { clearSearch, filteredItems } = useToolbarSearch({
		view: editor.view!,
		searchTerm: search,
		trigger,
		commands: editor.commands,
		items,
		filterable,
		uploadEnabled: !!editor.options.uploadFile,
	});

	const {
		fileUploadInputRef,
		handleFilePicked,
		handleItemClick,
		embedItem,
		insertBlock,
		preselectionWidget,
	} = useCommandMenu({
		editor,
		view: editor.view!,
		commands: editor.commands,
		onClearSearch: clearSearch,
		onClose: () => editor.closeToolbars(toolbar),
		onLinkToolbarOpen: () => editor.openToolbar(Toolbar.Link),
		onPlaceholderToolbarOpen: () => editor.openToolbar(Toolbar.Placeholder),
		dictionary,
		onFileUploadStart: editor.options.onFileUploadStart,
		onFileUploadStop: editor.options.onFileUploadStop,
		uploadFile: editor.options.uploadFile,
		onShowToast: editor.options.onShowToast,
		onTrackEvent: editor.options.onTrackEvent,
		isTemplate: !!editor.options.template,
		targetRef: refs.domReference.current,
	});

	useEffect(() => {
		reset();
	}, [embedItem, filteredItems, reset, preselectionWidget]);

	return (
		<>
			{embedItem && (
				<LinkInput
					placeholder={
						embedItem.title
							? dictionary.pasteLinkWithTitle(embedItem.title)
							: dictionary.pasteLink
					}
					autoFocus
					dictionary={dictionary}
					embedItem={embedItem}
					onInsertBlock={insertBlock}
					onClose={() => editor.closeToolbars(toolbar)}
					onShowToast={editor.options.onShowToast}
				/>
			)}
			{preselectionWidget}
			{!embedItem && !preselectionWidget && (
				<Box className={classes.scroll}>
					{filteredItems.map((item, index) => {
						if (item.name === 'separator') {
							return (
								<CommandMenuDivider
									// eslint-disable-next-line react/no-array-index-key
									key={index}
									index={index}
									title={item.title}
								/>
							);
						}

						return renderMenuItem(item, index, {
							'data-hovered': activeIndex === index ? true : undefined,
							'aria-selected': activeIndex === index ? true : undefined,
							'data-testid': `${item.name}-menu-item-${index}`,
							...getItemProps({
								active: activeIndex === index,
								role: 'option',
								id: `${item.name}-menu-item-${index}`,
								ref: (node: HTMLButtonElement) => {
									elementsRef.current[index] = node;
								},
								onClick: () => handleItemClick(item),
							}),
						});
					})}
				</Box>
			)}
			{editor.options.uploadFile && (
				<VisuallyHidden>
					<label>
						<input
							aria-label="Import document"
							type="file"
							ref={fileUploadInputRef}
							onChange={handleFilePicked}
						/>
					</label>
				</VisuallyHidden>
			)}
		</>
	);
}

export function CommandMenu(props: CommandMenuProps) {
	const { id, editor, toolbar } = props;

	return (
		<FloatingToolbar
			onClose={() => editor.closeToolbars(toolbar)}
			view={editor.view!}
			placement="top-start"
			menu-id={id}
		>
			<CommandMenuInternal {...props} />
		</FloatingToolbar>
	);
}
